import {
  format,
  parseISO,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear
} from 'date-fns';
import { getDateFromMagicKeyword, sendRequest } from './dateHelpers';
import { calculations, operations, periods } from '../constants';

export const formatDateWithPattern = (date, formatter) => {
  if (parseDateISO(date).toString() === 'Invalid Date' || formatter === '') {
    return date;
  }
  const d = parseDateISO(date);
  return format(d, formatter);
};

export const defaultPickerDate = () => {
  return {
    dateRange: {
      startDate: getDateFromMagicKeyword('last_12_month_first_day'),
      endDate: getDateFromMagicKeyword('now')
    },
    operation: operations.GIT_EFFORT,
    calculationsMethod: calculations.RATIO
  };
};

export const getDateHeader = (date, period) => {
  const d = new Date(date);
  switch (period) {
    case 'month':
      return format(d, 'MMM yyyy');
    case 'year':
      return format(d, 'yyyy');
    case 'week':
    default:
      return format(d, 'dd MMM yyyy');
  }
};

export function getPeriodRange(
  startDate = new Date(),
  endDate = new Date(),
  period
) {
  switch (period) {
    case 'week':
      return {
        startDate: formatDateWithPattern(
          startOfWeek(startDate, { weekStartsOn: 1 }),
          'yyyy-MM-dd'
        ),
        endDate: formatDateWithPattern(
          endOfWeek(endDate, { weekStartsOn: 1 }),
          'yyyy-MM-dd'
        )
      };
    case 'month':
      return {
        startDate: formatDateWithPattern(startOfMonth(startDate), 'yyyy-MM-dd'),
        endDate: formatDateWithPattern(endOfMonth(endDate), 'yyyy-MM-dd')
      };
    case 'year':
      return {
        startDate: formatDateWithPattern(startOfYear(startDate), 'yyyy-MM-dd'),
        endDate: formatDateWithPattern(endOfYear(endDate), 'yyyy-MM-dd')
      };
    default:
      return {
        startDate: startDate,
        endDate: endDate
      };
  }
}

export const customFormatDate = date => {
  const formattedDate = new Date(date);

  // Check if the date is valid
  if (isNaN(formattedDate)) {
    return 'Invalid Date';
  }

  const formatter = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return formatter.format(formattedDate);
};

export const formatNumber = number => {
  if (typeof number === 'string' || isNaN(number)) {
    return number;
  }

  const roundedNumber = Math.round(number);

  const formatter = new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });

  return formatter.format(roundedNumber);
};

const getHighchartsSeriesBaseKey = seriesLike => {
  if (!seriesLike) return '';

  return (
    seriesLike.linkedTo ||
    seriesLike?.options?.linkedTo ||
    seriesLike?.linkedParent?.options?.id ||
    seriesLike.id ||
    seriesLike?.options?.id ||
    seriesLike.name ||
    seriesLike?.options?.name ||
    ''
  );
};

const getSeriesPointValue = (seriesLike, pointIndex) => {
  if (!Number.isInteger(pointIndex) || pointIndex < 0 || !seriesLike) {
    return undefined;
  }

  const pointFromSeries = seriesLike?.data?.[pointIndex];
  if (
    pointFromSeries &&
    typeof pointFromSeries === 'object' &&
    'y' in pointFromSeries
  ) {
    return pointFromSeries.y;
  }
  if (pointFromSeries !== undefined) {
    return pointFromSeries;
  }

  const pointFromOptions = seriesLike?.options?.data?.[pointIndex];
  if (
    pointFromOptions &&
    typeof pointFromOptions === 'object' &&
    'y' in pointFromOptions
  ) {
    return pointFromOptions.y;
  }

  return pointFromOptions;
};

const getTooltipSeriesPriority = (seriesLike, pointIndex) => {
  if (!seriesLike) return -1;

  let priority = 0;
  const isLinkedSeries = Boolean(
    seriesLike.linkedTo || seriesLike?.options?.linkedTo
  );
  const mouseTrackingDisabled =
    seriesLike.enableMouseTracking === false ||
    seriesLike?.options?.enableMouseTracking === false;
  const pointValue = getSeriesPointValue(seriesLike, pointIndex);

  if (pointValue !== null && pointValue !== undefined) priority += 10;
  if (!isLinkedSeries) priority += 4;
  if (!mouseTrackingDisabled) priority += 2;

  return priority;
};

export const getUniqueTooltipPoints = points => {
  const pointsByKey = new Map();

  (Array.isArray(points) ? points : []).forEach(point => {
    if (!point?.series) return;

    const pointIndex = Number.isInteger(point?.point?.index)
      ? point.point.index
      : point?.index;
    const baseKey = getHighchartsSeriesBaseKey(point.series);
    const pointKey = `${baseKey}::${point?.x ?? point?.category ?? ''}`;
    const existingPoint = pointsByKey.get(pointKey);

    if (!existingPoint) {
      pointsByKey.set(pointKey, point);
      return;
    }

    const existingPointIndex = Number.isInteger(existingPoint?.point?.index)
      ? existingPoint.point.index
      : existingPoint?.index;
    const currentPriority = getTooltipSeriesPriority(point.series, pointIndex);
    const existingPriority = getTooltipSeriesPriority(
      existingPoint.series,
      existingPointIndex
    );

    if (currentPriority > existingPriority) {
      pointsByKey.set(pointKey, point);
    }
  });

  return [...pointsByKey.values()];
};

export const getUniqueTooltipSeries = (seriesList, pointIndex = null) => {
  const seriesByKey = new Map();

  (Array.isArray(seriesList) ? seriesList : []).forEach(series => {
    if (!series || series.visible === false) return;

    const baseKey = getHighchartsSeriesBaseKey(series);
    const existingSeries = seriesByKey.get(baseKey);

    if (!existingSeries) {
      seriesByKey.set(baseKey, series);
      return;
    }

    const currentPriority = getTooltipSeriesPriority(series, pointIndex);
    const existingPriority = getTooltipSeriesPriority(
      existingSeries,
      pointIndex
    );

    if (currentPriority > existingPriority) {
      seriesByKey.set(baseKey, series);
    }
  });

  return [...seriesByKey.values()];
};

export const parseDateISO = dateTimeStr => {
  if (typeof dateTimeStr !== 'string') {
    return new Date(dateTimeStr);
  }
  return parseISO(dateTimeStr);
};

export const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

export const clearAuthSession = () => {
  localStorage.removeItem('authToken');
  sessionStorage.removeItem('authToken');

  document.cookie.split(';').forEach(cookie => {
    const [rawName] = cookie.split('=');
    const name = rawName?.trim();

    if (!name) return;

    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname}`;

    const hostnameParts = window.location.hostname.split('.');
    if (hostnameParts.length > 2) {
      const rootDomain = hostnameParts.slice(-2).join('.');
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${rootDomain}`;
    }
  });
};

export const transformProjectData = data => {
  if (!data) return;
  const result = {};
  data?.forEach(item => {
    const { uid } = item;
    result[uid] = item;
  });
  return result;
};

export const calculateEndDate = (startDate, period) => {
  const date = new Date(startDate);

  switch (period) {
    case periods.DAY:
      //date.setDate(date.getDate() + 1);
      break;
    case periods.WEEK:
      date.setDate(date.getDate() + 6);
      break;
    case periods.MONTH:
      date.setMonth(date.getMonth() + 1);
      date.setDate(date.getDate() - 1);

      break;
    case periods.YEAR:
      date.setFullYear(date.getFullYear() + 1);
      date.setDate(date.getDate() - 1);
      break;
  }

  return date.toISOString().split('T')[0];
};

export const isIncompletePeriod = (
  startDate,
  period,
  referenceDate = new Date()
) => {
  if (!startDate || !period) return false;

  const start = new Date(startDate);
  const end = new Date(calculateEndDate(startDate, period));
  const reference =
    typeof referenceDate === 'string'
      ? new Date(referenceDate)
      : new Date(referenceDate);

  if ([start, end, reference].some(date => Number.isNaN(date.getTime()))) {
    return false;
  }

  return reference >= start && reference <= end;
};

export const validateEmail = email => {
  const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
  return emailRegex.test(email);
};

export const getPercentage = (value, total) => {
  if (total === 0) return 0;
  return Math.round((value / total) * 100);
};

const parseDateCandidate = candidate => {
  if (!candidate && candidate !== 0) return null;

  if (candidate instanceof Date) {
    return Number.isNaN(candidate.getTime()) ? null : candidate;
  }

  if (typeof candidate === 'number') {
    const numericDate = new Date(candidate);
    return Number.isNaN(numericDate.getTime()) ? null : numericDate;
  }

  if (typeof candidate === 'string') {
    const value = candidate.trim();
    if (!value) return null;

    if (/^\d{4}$/.test(value)) {
      const yearDate = new Date(`${value}-01-01T00:00:00`);
      return Number.isNaN(yearDate.getTime()) ? null : yearDate;
    }

    if (/^\d{4}-\d{2}$/.test(value)) {
      const monthDate = new Date(`${value}-01T00:00:00`);
      return Number.isNaN(monthDate.getTime()) ? null : monthDate;
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const dayDate = new Date(`${value}T00:00:00`);
      return Number.isNaN(dayDate.getTime()) ? null : dayDate;
    }

    const parsedDate = new Date(value);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  return null;
};

export const getSuggestedPeriodByDataRecency = (
  dateCandidates = [],
  {
    thresholdMonths = 2,
    recentPeriod = periods.WEEK,
    olderPeriod = periods.MONTH,
    fallbackPeriod = periods.MONTH,
    referenceDate = new Date()
  } = {}
) => {
  const parsedDates = (dateCandidates || [])
    .map(parseDateCandidate)
    .filter(Boolean);

  if (!parsedDates.length) {
    return fallbackPeriod;
  }

  const reference = parseDateCandidate(referenceDate) || new Date();
  const cutoffDate = new Date(reference);
  cutoffDate.setMonth(cutoffDate.getMonth() - thresholdMonths);

  const hasOlderThanThreshold = parsedDates.some(date => date < cutoffDate);

  return hasOlderThanThreshold ? olderPeriod : recentPeriod;
};

export const fetchData = async (filters, dataGroups, url = '/data') => {
  const filterTemp = filters;

  const filterData = {
    filters: filterTemp,
    dataGroups: dataGroups
  };

  const responseData = await sendRequest(url, 'POST', filterData);
  return responseData.data;
};

export const addUrlParams = (key, value) => {
  const params = new URLSearchParams(window.location.search);

  params.set(key, value);

  const newUrl = `${window.location.pathname}?${params.toString()}`;
  window.history.pushState({}, '', newUrl);
};

export const arrayToQueryString = (array, name, returnString) => {
  const params = new URLSearchParams(window.location.search);

  if (!Array.isArray(array)) {
    array = [{ ...array }];
  }

  const keysToDelete = [];
  for (const key of params.keys()) {
    if (key.startsWith(name)) keysToDelete.push(key);
  }

  keysToDelete.forEach(key => params.delete(key));

  for (let i = 0; i < array.length; i++) {
    params.append(`${name}[${i}]`, array[i].value);
  }

  if (returnString) {
    return params.toString();
  }

  const newUrl = `${window.location.pathname}?${params.toString()}`;
  window.history.pushState({}, '', newUrl);
};

export const addToQueryString = (string, name) => {
  const params = new URLSearchParams(window.location.search);

  params.delete(name);
  params.append(`${name}`, string);

  const newUrl = `${window.location.pathname}?${params.toString()}`;
  window.history.pushState({}, '', newUrl);
};

export const getParamFromQuery = paramName => {
  const query = new URLSearchParams(window.location.search);

  const values = [];
  let singleValue = null;

  query.forEach((value, key) => {
    // Match keys with the pattern paramName or paramName[index]
    const regex = new RegExp(`^${paramName}(\\[\\d+\\])?$`);
    if (regex.test(key)) {
      if (key === paramName) {
        // Handle the single value case directly
        singleValue = value;
      } else {
        // Collect values for array-like keys
        values.push(value);
      }
    }
  });

  // Decide the return value based on whether we have an array-like or single value
  if (singleValue !== null) {
    // If there is a single value directly, use it
    return values.length > 0 ? values : singleValue;
  } else {
    // If no single value, return the collected array
    return values.length > 0 ? values : null;
  }
};

export const effortGrades = [
  {
    effortMin: 0,
    effortMax: 400,
    label: 'Minimal',
    contributionGrade: 'no_visible_contribution',
    description:
      'No relevant code activity detected. May be inactive or on leave.',
    colorLabel: 'Gray',
    hex: '#E0E0E0',
    hexDark: '#334155',
    textColor: '#334155',
    textColorDark: '#e2e8f0',
    borderColor: '#CBD5E1',
    borderColorDark: '#475569'
  },
  {
    effortMin: 401,
    effortMax: 1000,
    label: 'Sporadic',
    contributionGrade: 'sporadic_contribution',
    description:
      'Irregular, low-volume input. May reflect context switching or ramp-up time.',
    colorLabel: 'Pale Yellow',
    hex: '#FFF9C4',
    hexDark: '#713F12',
    textColor: '#713F12',
    textColorDark: '#FEF3C7',
    borderColor: '#FDE68A',
    borderColorDark: '#92400E'
  },
  {
    effortMin: 1001,
    effortMax: 1800,
    label: 'Light',
    contributionGrade: 'light_contribution',
    description:
      'Continuous but light involvement. Completes minor or isolated tasks.',
    colorLabel: 'Mint Green',
    hex: '#DCEDC8',
    hexDark: '#14532D',
    textColor: '#365314',
    textColorDark: '#DCFCE7',
    borderColor: '#BBF7D0',
    borderColorDark: '#166534'
  },
  {
    effortMin: 1801,
    effortMax: 2800,
    label: 'Standard',
    contributionGrade: 'moderate_contribution',
    description: 'Steady and reliable participation. Baseline productivity.',
    colorLabel: 'Soft Green',
    hex: '#AED5B1',
    hexDark: '#166534',
    textColor: '#166534',
    textColorDark: '#DCFCE7',
    borderColor: '#86EFAC',
    borderColorDark: '#15803D'
  },
  {
    effortMin: 2801,
    effortMax: 4200,
    label: 'Substantial',
    contributionGrade: 'substantial_contribution',
    description:
      'Strong contributor. Delivers features, fixes, or reviews regularly.',
    colorLabel: 'Lime Green',
    hex: '#81C784',
    hexDark: '#047857',
    textColor: '#166534',
    textColorDark: '#D1FAE5',
    borderColor: '#6EE7B7',
    borderColorDark: '#059669'
  },
  {
    effortMin: 4201,
    effortMax: 6000,
    label: 'High',
    contributionGrade: 'high_contribution',
    description:
      'Well-above-average effort. Core to sprint delivery or team momentum.',
    colorLabel: 'Yellow Orange',
    hex: '#FFB74D',
    hexDark: '#9A3412',
    textColor: '#9A3412',
    textColorDark: '#FED7AA',
    borderColor: '#FDBA74',
    borderColorDark: '#C2410C'
  },
  {
    effortMin: 6001,
    effortMax: 8000,
    label: 'Very High',
    contributionGrade: 'very_high_contribution',
    description:
      'Significant month. Delivers broadly across systems or modules.',
    colorLabel: 'Amber',
    hex: '#FFA000',
    hexDark: '#B45309',
    textColor: '#92400E',
    textColorDark: '#FDE68A',
    borderColor: '#FBBF24',
    borderColorDark: '#D97706'
  },
  {
    effortMin: 8001,
    effortMax: 10000,
    label: 'Exceptional',
    contributionGrade: 'exceptional_contribution',
    description:
      'Highly productive month. Possibly reflects deadlines or urgent milestones.',
    colorLabel: 'Deep Orange',
    hex: '#F57C00',
    hexDark: '#C2410C',
    textColor: '#9A3412',
    textColorDark: '#FED7AA',
    borderColor: '#FB923C',
    borderColorDark: '#EA580C'
  },
  {
    effortMin: 10001,
    effortMax: Infinity,
    label: 'Extreme',
    contributionGrade: 'extraordinary_contribution',
    description:
      'Extremely rare. Consistent value but may need balance review.',
    colorLabel: 'Crimson Red',
    hex: '#D32F2F',
    hexDark: '#991B1B',
    textColor: '#991B1B',
    textColorDark: '#FEE2E2',
    borderColor: '#FCA5A5',
    borderColorDark: '#B91C1C'
  }
];

export function getContributionDetailsByEffort(effort) {
  return effortGrades.find(
    grade => effort >= grade.effortMin && effort <= grade.effortMax
  );
}

export const findOpenPaths = (items, pathname, parents = []) => {
  for (const item of items) {
    if (item.path === pathname) {
      return parents;
    }
    if (item.nodes) {
      const result = findOpenPaths(item.nodes, pathname, [
        ...parents,
        item.label
      ]);
      if (result) return result;
    }
  }
  return null;
};
